/**
 * PROA Comparison Selector
 *
 * Chip-based selector for comparing DRE actuals against PROA budget scenarios.
 * Features:
 * - Toggle to enable/disable PROA comparison
 * - Chip selector for multiple scenario selection
 * - Auto-selects official PROA by default when enabled
 * - Styled for Monutrack dark theme
 */

import { Crown } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useListScenarios, type ProaScenario } from '../../proa/hooks/useProaData'

export interface ProaComparisonState {
  enabled: boolean
  selectedScenarioIds: string[]
}

interface ProaComparisonSelectorProps {
  /** Year to fetch scenarios for (derived from selected period) */
  year: number
  /** Current PROA comparison state */
  value: ProaComparisonState
  /** Callback when state changes */
  onChange: (state: ProaComparisonState) => void
  /** Whether the control is disabled */
  disabled?: boolean
}

/**
 * PROA Comparison Selector Component
 *
 * Provides a toggle to enable PROA comparison and chip-based
 * multi-select for choosing which scenarios to compare against.
 */
export function ProaComparisonSelector({
  year,
  value,
  onChange,
  disabled = false,
}: ProaComparisonSelectorProps) {
  const { data: scenarios = [], isLoading } = useListScenarios(year)

  // Find official scenario
  const officialScenario = useMemo(
    () => scenarios.find((s) => s.isOfficial),
    [scenarios]
  )

  // Auto-select official scenario when enabling PROA comparison
  useEffect(() => {
    if (value.enabled && value.selectedScenarioIds.length === 0 && officialScenario) {
      onChange({
        enabled: true,
        selectedScenarioIds: [officialScenario.scenarioId],
      })
    }
  }, [value.enabled, value.selectedScenarioIds.length, officialScenario, onChange])

  const handleToggle = (enabled: boolean) => {
    if (enabled) {
      // When enabling, auto-select official scenario if available
      const defaultSelection = officialScenario ? [officialScenario.scenarioId] : []
      onChange({ enabled: true, selectedScenarioIds: defaultSelection })
    } else {
      onChange({ enabled: false, selectedScenarioIds: [] })
    }
  }

  const handleScenarioToggle = (scenarioId: string) => {
    const isSelected = value.selectedScenarioIds.includes(scenarioId)

    if (isSelected) {
      // Don't allow deselecting the last scenario
      if (value.selectedScenarioIds.length === 1) return

      onChange({
        ...value,
        selectedScenarioIds: value.selectedScenarioIds.filter((id) => id !== scenarioId),
      })
    } else {
      onChange({
        ...value,
        selectedScenarioIds: [...value.selectedScenarioIds, scenarioId],
      })
    }
  }

  // Get selected scenarios in order
  const selectedScenarios = useMemo(() => {
    return value.selectedScenarioIds
      .map((id) => scenarios.find((s) => s.scenarioId === id))
      .filter((s): s is ProaScenario => s !== undefined)
  }, [value.selectedScenarioIds, scenarios])

  return (
    <div className="flex flex-col gap-3">
      {/* Toggle Row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Disabled Button */}
        <button
          onClick={() => handleToggle(false)}
          disabled={disabled}
          className={`
            px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-[#D2FA52]
            ${
              !value.enabled
                ? 'border-2 border-[#D2FA52] bg-[#D2FA52]/20 text-[#D2FA52] font-semibold'
                : disabled
                  ? 'border border-[#3A3D44] bg-[#2A2D34] text-[#5A5D66] cursor-not-allowed opacity-60'
                  : 'border border-[#3A3D44] bg-[#2A2D34] text-[#838998] hover:bg-[#3A3D44] hover:text-[#D2FA52] cursor-pointer'
            }
          `}
        >
          ○ Desativado
        </button>

        {/* Enabled Button */}
        <button
          onClick={() => handleToggle(true)}
          disabled={disabled || scenarios.length === 0}
          className={`
            px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-[#D2FA52]
            ${
              value.enabled
                ? 'border-2 border-[#D2FA52] bg-[#D2FA52]/20 text-[#D2FA52] font-semibold'
                : disabled || scenarios.length === 0
                  ? 'border border-[#3A3D44] bg-[#2A2D34] text-[#5A5D66] cursor-not-allowed opacity-60'
                  : 'border border-[#3A3D44] bg-[#2A2D34] text-[#838998] hover:bg-[#3A3D44] hover:text-[#D2FA52] cursor-pointer'
            }
          `}
          title={scenarios.length === 0 ? 'Nenhum cenário PROA disponível para este ano' : undefined}
        >
          ● Comparar com PROA
        </button>

        {/* Loading indicator */}
        {isLoading && (
          <span className="text-xs text-[#838998] animate-pulse">Carregando cenários...</span>
        )}

        {/* No scenarios warning */}
        {!isLoading && scenarios.length === 0 && (
          <span className="text-xs text-[#838998]">
            Nenhum cenário PROA para {year}
          </span>
        )}
      </div>

      {/* Scenario Chips Row - Only show when enabled */}
      {value.enabled && scenarios.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap pl-0">
          {scenarios.map((scenario) => {
            const isSelected = value.selectedScenarioIds.includes(scenario.scenarioId)
            const isLastSelected = isSelected && value.selectedScenarioIds.length === 1

            return (
              <button
                key={scenario.scenarioId}
                onClick={() => handleScenarioToggle(scenario.scenarioId)}
                disabled={disabled || isLastSelected}
                title={
                  isLastSelected
                    ? 'Pelo menos um cenário deve estar selecionado'
                    : scenario.isOfficial
                      ? 'Cenário oficial'
                      : undefined
                }
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                  transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#D2FA52]
                  ${
                    isSelected
                      ? 'border-2 border-[#D2FA52] bg-[#D2FA52]/20 text-[#D2FA52]'
                      : 'border border-[#3A3D44] bg-[#2A2D34] text-[#838998] hover:bg-[#3A3D44] hover:text-[#D2FA52]'
                  }
                  ${isLastSelected ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {scenario.isOfficial && (
                  <Crown className="h-3.5 w-3.5" />
                )}
                {scenario.name}
                {isSelected && (
                  <span className="ml-0.5">✓</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Helper to get display name for a scenario (used in column headers)
 */
export function getScenarioDisplayName(scenario: ProaScenario): string {
  return scenario.isOfficial ? `PROA ${scenario.year}` : scenario.name
}

export default ProaComparisonSelector
